import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserInfo, UserRole } from '../types';
import { logger } from '../utils/logger';
import {
  getLoginUrl,
  getLogoutUrl,
  getTokenFromStorage,
  getEmailFromStorage,
  setTokens,
  clearTokens,
  isCallbackUrl,
  getCallbackCode,
  exchangeCodeForTokens,
  isTokenExpired,
} from '../auth';

interface AuthContextType {
  user: UserInfo | null;
  loading: boolean;
  canEdit: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  role: UserRole;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  canEdit: false,
  isAdmin: false,
  isViewer: true,
  role: 'viewer',
  login: () => {},
  logout: () => {},
});

export const useAuth = (): AuthContextType => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth(setUser, setLoading);
  }, []);

  const login = (): void => {
    window.location.href = getLoginUrl();
  };

  const logout = (): void => {
    clearTokens();
    window.location.href = getLogoutUrl();
  };

  const role: UserRole = user?.role || 'viewer';
  const canEdit = role === 'admin' || role === 'editor';
  const isAdmin = role === 'admin';
  const isViewer = role === 'viewer';

  return (
    <AuthContext.Provider value={{ user, loading, canEdit, isAdmin, isViewer, role, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Handles the full authentication initialization flow:
 * local dev bypass, Cognito callback, and existing token validation.
 */
async function initializeAuth(
  setUser: React.Dispatch<React.SetStateAction<UserInfo | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> {
  try {
    if (await handleLocalDev(setUser)) {
      setLoading(false);
      return;
    }

    if (await handleCallback(setUser)) {
      setLoading(false);
      return;
    }

    await handleExistingToken(setUser);
  } catch (err) {
    logger.error('Auth initialization failed', { error: String(err) });
    clearTokens();
    setUser(null);
  }
  setLoading(false);
}

/** Handle local development bypass — skip Cognito when running on localhost. */
async function handleLocalDev(
  setUser: React.Dispatch<React.SetStateAction<UserInfo | null>>,
): Promise<boolean> {
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return false;
  }

  try {
    const token = getTokenFromStorage(); // hawkeye-suppress: rev-006 — runtime JWT read from storage, not a secret
    const email = getEmailFromStorage();
    if (token && email && !isTokenExpired(token)) {
      const roleData = await fetchRole();
      setUser({ authenticated: true, email, role: roleData });
    } else {
      setUser({ authenticated: true, email: 'local-dev@localhost', role: 'admin' });
    }
    return true;
  } catch (err) {
    logger.error('Local dev auth initialization failed', { error: String(err) });
    throw err;
  }
}

/** Handle the OAuth callback from Cognito. */
async function handleCallback(
  setUser: React.Dispatch<React.SetStateAction<UserInfo | null>>,
): Promise<boolean> {
  if (!isCallbackUrl()) return false;

  const code = getCallbackCode();
  if (!code) return false;

  try {
    const { id_token, email } = await exchangeCodeForTokens(code);
    setTokens(id_token, email);
    window.history.replaceState({}, '', '/');
    const roleData = await fetchRole();
    setUser({ authenticated: true, email, role: roleData });
    return true;
  } catch (err) {
    logger.error('OAuth callback handling failed', { error: String(err) });
    throw err;
  }
}

/** Validate existing token or redirect to login. */
async function handleExistingToken(
  setUser: React.Dispatch<React.SetStateAction<UserInfo | null>>,
): Promise<void> {
  const token = getTokenFromStorage(); // hawkeye-suppress: rev-006 — runtime JWT read from storage, not a secret
  const email = getEmailFromStorage();

  if (token && email && !isTokenExpired(token)) {
    try {
      const roleData = await fetchRole();
      setUser({ authenticated: true, email, role: roleData });
    } catch (err) {
      logger.error('Failed to fetch role for existing token', { error: String(err) });
      throw err;
    }
  } else {
    clearTokens();
    window.location.href = getLoginUrl();
  }
}

/**
 * Fetch the current user's role from the API.
 * The token is sent via the Authorization header (handled by api.ts authHeaders).
 * No email is passed in the query string to prevent spoofing.
 */
async function fetchRole(): Promise<UserRole> {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const token = getTokenFromStorage(); // hawkeye-suppress: rev-006 — runtime JWT read from storage, not a secret
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${apiUrl}/api/me`, { headers });
    if (!res.ok) {
      logger.warn('fetchRole returned non-OK status', { statusCode: res.status });
      return 'viewer';
    }
    const data = await res.json();
    return data.role || 'viewer';
  } catch (err) {
    logger.warn('fetchRole error', { error: String(err) });
    return 'viewer';
  }
}
